import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useTran } from "@/hooks";

const isTco2eUnit = (unit) =>
  String(unit || "")
    .trim()
    .toLowerCase() === "tco2e";

const getActivityTco2eTotal = (activity) => {
  const entries = Array.isArray(activity?.entries) ? activity.entries : [];
  const values = entries
    .filter((e) => isTco2eUnit(e?.unit))
    .map((e) => Number(e?.quantity) || 0);
  if (values.length === 0) return null;
  return values.reduce((a, b) => a + b, 0);
};

const EvidenceList = ({ files = [] }) => {
  const list = Array.isArray(files) ? files : [];
  if (list.length === 0) return <div className="text-xs text-slate-500">—</div>;
  return (
    <div className="space-y-1">
      {list.map((f, idx) => (
        <div key={`${f.fileId || f.url || idx}`} className="text-xs">
          {f.url ? (
            <a
              href={f.url}
              target="_blank"
              rel="noreferrer"
              className="truncate text-nxstart-blue hover:underline"
            >
              {f.originalName || "Evidence file"}
            </a>
          ) : (
            <span className="truncate text-slate-600 dark:text-slate-300">
              {f.originalName || "Evidence file"}
            </span>
          )}
        </div>
      ))}
    </div>
  );
};

const periodLabel = (period) => {
  if (period === "quarterly") return "Quarterly total";
  if (period === "monthly") return "Monthly entries";
  return "Annual total";
};

const EntryGrid = ({ entries = [] }) => (
  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
    {entries.map((entry, idx) => (
      <div
        key={entry.key || idx}
        className="rounded-md border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-950/20"
      >
        <div className="text-sm font-semibold text-slate-900 dark:text-white">
          {entry.label || "—"}
        </div>
        <div className="mt-2 grid grid-cols-3 gap-2 text-sm">
          <div className="col-span-2">
            <div className="text-xs font-medium text-slate-500 dark:text-slate-400">
              Quantity
            </div>
            <div className="mt-1 text-slate-900 dark:text-white">
              {entry.quantity || "—"}
            </div>
          </div>
          <div>
            <div className="text-xs font-medium text-slate-500 dark:text-slate-400">
              Unit
            </div>
            <div className="mt-1 text-slate-900 dark:text-white">
              {entry.unit || "—"}
            </div>
          </div>
        </div>
        <div className="mt-3">
          <div className="text-xs font-medium text-slate-500 dark:text-slate-400">{`Evidence (${entry.label || "period"})`}</div>
          <div className="mt-1">
            <EvidenceList files={entry.evidenceFiles || []} />
          </div>
        </div>
      </div>
    ))}
  </div>
);

const CarbonActivityReportCard = ({ title, hint, activity }) => {
  const period = activity?.period || "annually";
  const entries = Array.isArray(activity?.entries) ? activity.entries : [];
  const totalTco2e = getActivityTco2eTotal(activity);
  const { t } = useTran();

  const annualEntry = entries[0] || {};

  return (
    <Card>
      <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <CardTitle className="text-base">{title}</CardTitle>
        </div>
        <div className="self-start">
          <Badge variant="outline">{periodLabel(period)}</Badge>
        </div>
      </CardHeader>
      <CardContent>
        {period === "annually" ? (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
            <div className="md:col-span-7">
              <div className="text-xs font-medium text-slate-500 dark:text-slate-400">
                {t("Annual value")}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-800 dark:bg-slate-950/20 dark:text-white">
                  {annualEntry.quantity || "—"}
                </div>
                <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-800 dark:bg-slate-950/20 dark:text-white">
                  {annualEntry.unit || "—"}
                </div>
              </div>
            </div>
            <div className="md:col-span-5">
              <div className="text-xs font-medium text-slate-500 dark:text-slate-400">
                {t("Evidence (annual summary)")}
              </div>
              <div className="mt-1">
                <EvidenceList files={annualEntry.evidenceFiles || []} />
              </div>
            </div>
          </div>
        ) : null}

        {period === "quarterly" ? <EntryGrid entries={entries} /> : null}
        {period === "monthly" ? <EntryGrid entries={entries} /> : null}
      </CardContent>
    </Card>
  );
};

export default CarbonActivityReportCard;
